/**
 * decisionPdf.js — PDF-ja e çdo vendimi, sipas strukturës që kompania e
 * vendos te "Shabllonet → Vendimet → Struktura e PDF-së".
 *
 * Më parë PDF-ja e ndërprerjes ishte e ngulur te Directory.js: koka e kuqe,
 * titulli, qyteti "Prishtinë" dhe neni "37" ishin në kod, pa logo e pa
 * nënshkrues. Tani:
 *   • `type`    (decision_types) jep titullin dhe bazën ligjore;
 *   • `layout`  (decision_settings) jep kokën, logon, ngjyrën, qytetin,
 *               nënshkruesin, tekstin përmbyllës dhe footer-in;
 *   • `company` (settings) mbush çdo fushë që layout-i e lë bosh.
 *
 * Asnjë thirrje rrjeti këtu — thirrësi i sjell të tria, që funksioni të mbetet
 * i testueshëm dhe i njëjtë për "Ndërpre Kontratën" dhe për gjeneruesin.
 */
import { jsPDF } from 'jspdf';

export const DEFAULT_LAYOUT = {
  headerTitle: '',            // bosh → emri i kompanisë
  headerSubtitle: '',         // bosh → adresa · NUI
  showLogo: true,
  accentColor: '#1E4E5A',
  city: '',                   // bosh → qyteti i kompanisë
  legalArticle: '37',
  numberPrefix: 'VND',
  showInfoTable: true,
  signatoryName: '',          // bosh → drejtori i kompanisë
  signatoryTitle: 'Drejtori',
  showEmployeeSignature: true,
  closingText: 'Ky vendim hyn në fuqi në ditën e nënshkrimit.',
  footerText: '',             // bosh → kompania · adresa · NUI
};

const MONTHS = ['janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'];

/** '2026-10-03' → '3 tetor 2026'; çdo gjë tjetër kthehet siç është. */
export function fmtDecisionDate(d) {
  if (!d) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (!m) return String(d);
  return `${parseInt(m[3], 10)} ${MONTHS[parseInt(m[2], 10) - 1] || ''} ${m[1]}`;
}

const todayKey = () => new Date().toISOString().slice(0, 10);

export function resolveLayout(layout, company) {
  const l = { ...DEFAULT_LAYOUT, ...(layout || {}) };
  const co = company || {};
  return {
    ...l,
    headerTitle: l.headerTitle || co.CompanyName || 'Kompania',
    headerSubtitle: l.headerSubtitle || [co.Address, co.NUI ? `NUI: ${co.NUI}` : ''].filter(Boolean).join('  ·  '),
    city: l.city || co.City || 'Prishtinë',
    signatoryName: l.signatoryName || co.Director || '',
    footerText: l.footerText || [co.CompanyName, co.Address, co.Phone, co.Email].filter(Boolean).join('  ·  '),
    logo: l.showLogo ? (co.Logo || co.logo || '') : '',
  };
}

const fullNameOf = (w) => `${w?.FirstName || ''} ${w?.LastName || ''}`.trim() || w?.FullName || '';

/**
 * Plotëson vendmbajtësit e shabllonit. [Vendi] dhe [XX] vinin të ngulur
 * ("Prishtinë", "37"); tani vijnë nga struktura, me të njëjtat vlera si parazgjedhje.
 */
export function fillDecisionPlaceholders(text, { worker, date, layout, company } = {}) {
  if (!text) return text;
  const l = resolveLayout(layout, company);
  const name = fullNameOf(worker);
  const eff = fmtDecisionDate(date);
  const today = fmtDecisionDate(todayKey());
  return String(text)
    .replace(/\[Emri Mbiemri\]/g, name)
    .replace(/\[Emri dhe Mbiemri\]/g, name)
    .replace(/\[Data Skadimit\]/g, eff)
    .replace(/\[Data e Dorëheqjes\]/g, eff)
    .replace(/\[Data Përfundimit\]/g, eff)
    .replace(/\[Data e Fillimit\]/g, eff)
    .replace(/\[Data e Njoftimit\]/g, today)
    .replace(/\[Data\]/g, today)
    .replace(/\[Pozita\]/g, worker?.Position || '')
    .replace(/\[Departamenti\]/g, worker?.DepartmentName || worker?.Department || '')
    .replace(/\[Kompania\]/g, company?.CompanyName || '')
    .replace(/\[Vendi\]/g, l.city)
    .replace(/\[XX\]/g, l.legalArticle || '[XX]');
}

/* Formati i logos nga bajtet — etiketa e data-URI-t gënjen te kjo bazë
 * (shih payslipPdf.js). */
function logoFormat(dataUrl) {
  const b = String(dataUrl || '').split(',')[1] || '';
  if (b.startsWith('/9j/')) return 'JPEG';
  if (b.startsWith('iVBOR')) return 'PNG';
  return String(dataUrl).includes('image/jpeg') ? 'JPEG' : 'PNG';
}

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || ''));
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [30, 78, 90];
}

/**
 * @param {object} p
 * @param {object} p.type     { name, title, legal_basis }
 * @param {object} p.layout   decision_settings.layout
 * @param {object} p.company  settings row (CompanyName, Logo, City, Director…)
 * @param {object} p.worker   { Id, FirstName, LastName, Position, DepartmentName }
 * @param {string} p.date     'YYYY-MM-DD' — data e hyrjes në fuqi
 * @param {string} p.body     teksti i vendimit, vendmbajtësit tashmë të plotësuar
 * @returns {jsPDF}
 */
export function buildDecisionPdf({ type = {}, layout, company, worker = {}, date, body = '', refCode } = {}) {
  const l = resolveLayout(layout, company);
  const accent = hexToRgb(l.accentColor);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const mL = 20, mR = 20, CW = PW - mL - mR;
  const name = fullNameOf(worker);
  const today = fmtDecisionDate(todayKey());
  const ref = refCode || `${l.numberPrefix || 'VND'}-${worker?.Id || '0'}-${Date.now().toString(36).toUpperCase()}`;
  let y = 16;

  // ── Koka: logo majtas, kompania pranë, numri e data djathtas ──────────────
  let textX = mL;
  if (l.logo) {
    try {
      const props = doc.getImageProperties(l.logo);
      const lh = 14;
      const lw = Math.min(lh * (props.width / props.height), 40);
      doc.addImage(l.logo, logoFormat(l.logo), mL, y - 5, lw, lh);
      textX = mL + lw + 4;
    } catch { /* pa logo — koka mbetet me tekst */ }
  }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12.5); doc.setTextColor(20, 20, 20);
  doc.text(l.headerTitle, textX, y);
  if (l.headerSubtitle) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(100, 100, 100);
    doc.text(doc.splitTextToSize(l.headerSubtitle, PW - mR - textX - 55), textX, y + 5);
  }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(70, 70, 70);
  doc.text(`Nr. ${ref}`, PW - mR, y, { align: 'right' });
  doc.text(`${l.city}, ${today}`, PW - mR, y + 5, { align: 'right' });
  y += 14;
  doc.setDrawColor(...accent); doc.setLineWidth(0.7);
  doc.line(mL, y, PW - mR, y);
  y += 10;

  // ── Baza ligjore ──────────────────────────────────────────────────────────
  if (type.legal_basis) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(40, 40, 40);
    const basis = String(type.legal_basis).trim().replace(/[:.]?$/, ':');
    const lines = doc.splitTextToSize(basis, CW);
    doc.text(lines, mL, y);
    y += lines.length * 4.8 + 6;
  }

  // ── Titulli ───────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...accent);
  const title = (type.title || type.name || 'VENDIM').toUpperCase();
  const tLines = doc.splitTextToSize(title, CW);
  doc.text(tLines, PW / 2, y, { align: 'center' });
  y += tLines.length * 6 + 6;

  // ── Të dhënat e punëmarrësit ──────────────────────────────────────────────
  if (l.showInfoTable) {
    const rows = [
      ['Punëmarrësi', name],
      ...(worker?.Position ? [['Pozita', worker.Position]] : []),
      ...((worker?.DepartmentName || worker?.Department) ? [['Departamenti', worker.DepartmentName || worker.Department]] : []),
      ...(date ? [['Data e hyrjes në fuqi', fmtDecisionDate(date)]] : []),
    ];
    const rH = 7.5, c1 = 52;
    rows.forEach((row, i) => {
      if (i % 2 === 0) { doc.setFillColor(247, 249, 250); doc.rect(mL, y - 5, CW, rH, 'F'); }
      doc.setDrawColor(222, 226, 230); doc.setLineWidth(0.18); doc.rect(mL, y - 5, CW, rH);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(95, 95, 95);
      doc.text(row[0], mL + 3, y);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      doc.text(String(row[1] ?? ''), mL + c1, y);
      y += rH;
    });
    y += 8;
  }

  // ── Teksti i vendimit ─────────────────────────────────────────────────────
  const ensureRoom = (h) => { if (y + h > PH - 30) { doc.addPage(); y = 22; } };
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.8); doc.setTextColor(30, 30, 30);
  String(body || '').split(/\n{1,}/).map((p) => p.trim()).filter(Boolean).forEach((para) => {
    doc.splitTextToSize(para, CW).forEach((line) => {
      ensureRoom(5);
      doc.text(line, mL, y);
      y += 5;
    });
    y += 2.5;
  });

  if (l.closingText) {
    y += 2;
    ensureRoom(8);
    doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(70, 70, 70);
    doc.splitTextToSize(l.closingText, CW).forEach((line) => { ensureRoom(5); doc.text(line, mL, y); y += 4.8; });
  }

  // ── Nënshkrimet ───────────────────────────────────────────────────────────
  ensureRoom(40);
  y += 14;
  const sigY = y + 12;
  doc.setDrawColor(50, 50, 50); doc.setLineWidth(0.35);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(60, 60, 60);
  if (l.showEmployeeSignature) {
    doc.line(mL, sigY, mL + 62, sigY);
    doc.text('I/E njoftuar', mL + 31, sigY + 5, { align: 'center' });
    doc.text(name, mL + 31, sigY + 9.5, { align: 'center' });
  }
  doc.line(PW - mR - 62, sigY, PW - mR, sigY);
  doc.text(l.signatoryTitle || 'Punëdhënësi', PW - mR - 31, sigY + 5, { align: 'center' });
  if (l.signatoryName) doc.text(l.signatoryName, PW - mR - 31, sigY + 9.5, { align: 'center' });

  // ── Footer në çdo faqe ────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.25);
    doc.line(mL, PH - 12, PW - mR, PH - 12);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.2); doc.setTextColor(150, 150, 150);
    doc.text(doc.splitTextToSize(l.footerText || '', CW - 30)[0] || '', mL, PH - 7);
    doc.text(`Faqe ${p}/${pages}`, PW - mR, PH - 7, { align: 'right' });
  }
  return doc;
}

/** Base64 e pastër — për bashkëngjitjen e email-it. */
export function decisionPdfBase64(params) {
  return buildDecisionPdf(params).output('datauristring').split(',')[1];
}

export function downloadDecisionPdf(params, filename) {
  buildDecisionPdf(params).save(filename || 'vendim.pdf');
}
